<template>
  <svg
    v-if="path"
    :viewBox="`0 0 ${width} ${height}`"
    class="refund-sparkline"
    preserveAspectRatio="none"
  >
    <defs>
      <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" :stop-color="color" stop-opacity="0.18" />
        <stop offset="100%" :stop-color="color" stop-opacity="0" />
      </linearGradient>
    </defs>
    <path :d="areaPath" :fill="`url(#${gradientId})`" stroke="none" />
    <path
      :d="path"
      fill="none"
      :stroke="color"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <circle
      v-if="lastPoint"
      :cx="lastPoint.x"
      :cy="lastPoint.y"
      r="2"
      :fill="color"
    />
  </svg>
</template>

<script>
export default {
  name: 'RefundSparkline',

  props: {
    data: {
      type: Array,
      default: () => [],
    },
    color: {
      type: String,
      default: '#104b94',
    },
    width: {
      type: Number,
      default: 100,
    },
    height: {
      type: Number,
      default: 28,
    },
  },

  computed: {
    gradientId() {
      return `refund-sparkline-grad-${this._uid}`;
    },
    values() {
      return this.data.map((v) => (v == null ? 0 : v));
    },
    coords() {
      if (this.values.length < 2) return [];
      const min = Math.min(...this.values);
      const max = Math.max(...this.values);
      const range = max - min || 1;
      // Keep the line inset from the top/bottom edges so it never clips
      // against the card's border, and so small fluctuations don't read
      // as sharp peaks touching the frame.
      const inset = this.height * 0.15;
      const usableHeight = this.height - inset * 2;
      const step = this.width / (this.values.length - 1);
      return this.values.map((v, i) => ({
        x: i * step,
        y: this.height - inset - ((v - min) / range) * usableHeight,
      }));
    },
    // Smooths the line by routing each segment through a cubic Bézier whose
    // control points sit at the segment's horizontal midpoint — avoids the
    // sharp point-to-point zigzag of a raw polyline without needing a
    // charting library.
    path() {
      const pts = this.coords;
      if (pts.length < 2) return '';
      let d = `M ${pts[0].x},${pts[0].y}`;
      for (let i = 1; i < pts.length; i += 1) {
        const prev = pts[i - 1];
        const curr = pts[i];
        const midX = (prev.x + curr.x) / 2;
        d += ` C ${midX},${prev.y} ${midX},${curr.y} ${curr.x},${curr.y}`;
      }
      return d;
    },
    areaPath() {
      if (!this.path) return '';
      const pts = this.coords;
      const last = pts[pts.length - 1];
      const first = pts[0];
      return `${this.path} L ${last.x},${this.height} L ${first.x},${this.height} Z`;
    },
    lastPoint() {
      const pts = this.coords;
      return pts.length ? pts[pts.length - 1] : null;
    },
  },
};
</script>

<style scoped>
.refund-sparkline {
  width: 100%;
  height: 28px;
  display: block;
  margin-top: 8px;
}
</style>
